import { transliterate } from "./cp437";
import {
	type Align,
	type Block,
	type Column,
	charsPerLine,
	forPaper,
	type PaperWidth,
	type TextStyle,
} from "./doc";

/**
 * Character-grid layout for ESC/POS text mode. Turns blocks into fixed-width
 * lines, already padded and aligned, so the printed output and the on-screen
 * preview come from exactly the same data.
 *
 * Moved from the proof of concept (`web/shared/layout.ts`).
 */

export type TextLine = {
	t: "line";
	/** Exactly `cols` characters wide, CP437 only. */
	text: string;
	style: TextStyle;
	cols: number;
};

export type Line =
	| TextLine
	| Exclude<Block, { t: "text" } | { t: "row" } | { t: "rule" } | { t: "byWidth" } | { t: "logo" }>;

/** Word-wraps to `width` characters. Honours explicit newlines and hard-breaks long words. */
export function wrap(text: string, width: number): string[] {
	if (width <= 0) return [""];
	const out: string[] = [];
	for (const para of text.split("\n")) {
		let line = "";
		for (const word of para.split(" ")) {
			let w = word;
			while (w.length > width) {
				if (line) {
					out.push(line);
					line = "";
				}
				out.push(w.slice(0, width));
				w = w.slice(width);
			}
			if (!line) line = w;
			else if (line.length + 1 + w.length <= width) line += ` ${w}`;
			else {
				out.push(line);
				line = w;
			}
		}
		out.push(line);
	}
	return out;
}

export function pad(text: string, width: number, align: Align = "left"): string {
	const s = text.slice(0, width);
	const gap = width - s.length;
	if (align === "right") return " ".repeat(gap) + s;
	if (align === "center") {
		const left = Math.floor(gap / 2);
		return " ".repeat(left) + s + " ".repeat(gap - left);
	}
	return s + " ".repeat(gap);
}

/** Splits `total` characters between columns by weight, leaving 1-character gutters. */
export function columnWidths(cols: Column[], total: number): number[] {
	const avail = total - (cols.length - 1);
	const sum = cols.reduce((a, c) => a + c.weight, 0);
	const widths = cols.map((c) => Math.max(1, Math.floor((c.weight / sum) * avail)));
	const last = widths.length - 1;
	widths[last] = (widths[last] ?? 0) + avail - widths.reduce((a, b) => a + b, 0);
	return widths;
}

/** Transliterates each line on its own, so explicit newlines survive. */
function printable(text: string): string {
	return text.split("\n").map(transliterate).join("\n");
}

export function layout(blocks: Block[], paper: PaperWidth): Line[] {
	const lines: Line[] = [];
	for (const b of forPaper(blocks, paper)) {
		// A logo not swapped for its bitmap (`withLogos`) is left out.
		if (b.t === "byWidth" || b.t === "logo") continue;
		if (b.t === "text") {
			const style = b.style ?? {};
			const cols = charsPerLine(paper, style.font, style.w);
			for (const l of wrap(printable(b.text), cols)) {
				lines.push({ t: "line", text: pad(l, cols, style.align), style, cols });
			}
		} else if (b.t === "row") {
			const style = b.style ?? {};
			const cols = charsPerLine(paper, style.font, style.w);
			const widths = columnWidths(b.cols, cols);
			const cells = b.cols.map((c, i) => wrap(printable(c.text), widths[i] ?? 1));
			const height = Math.max(...cells.map((c) => c.length));
			for (let r = 0; r < height; r++) {
				const text = b.cols
					.map((c, i) => pad(cells[i]?.[r] ?? "", widths[i] ?? 1, c.align))
					.join(" ");
				lines.push({ t: "line", text, style, cols });
			}
		} else if (b.t === "rule") {
			const cols = charsPerLine(paper);
			lines.push({ t: "line", text: (b.ch ?? "-").repeat(cols), style: {}, cols });
		} else {
			lines.push(b);
		}
	}
	return lines;
}
